"use client"

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { BookOpen, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordsDoNotMatch', 'As senhas não coincidem.'));
      return;
    }

    if (!token) {
      setError(t('resetPassword.invalidTokenRequestNew', 'Token inválido. Solicite um novo link de redefinição.'));
      return;
    }

    setIsLoading(true);
    try {
      await api.resetPassword(token, password);
      setSuccess(true);
      // Redirect to login after 2.5s
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: string }).message)
        : t('resetPassword.invalidOrExpiredToken', 'Token inválido ou expirado. Solicite um novo link.');
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <BookOpen className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">reformu.lat</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('resetPassword.professionalPlatform', 'Plataforma profissional de escrita de livros')}
          </p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">{t('resetPassword.newPassword', 'Nova senha')}</CardTitle>
            <CardDescription>
              {t('resetPassword.chooseSecurePassword', 'Escolha uma senha segura com pelo menos 8 caracteres.')}
            </CardDescription>
          </CardHeader>

          {!token ? (
            <CardContent>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t('resetPassword.invalidLinkRequestNew', 'Link inválido. Solicite um novo link de redefinição de senha.')}
                </AlertDescription>
              </Alert>
            </CardContent>
          ) : success ? (
            <CardContent>
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <CheckCircle2 className="h-10 w-10 text-primary" />
                <p className="font-medium">{t('resetPassword.passwordResetSuccess', 'Senha redefinida com sucesso!')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('resetPassword.redirectingToLogin', 'Você será redirecionado para o login em instantes...')}
                </p>
              </div>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">{t('resetPassword.newPassword', 'Nova senha')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('resetPassword.confirmNewPassword', 'Confirmar nova senha')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    disabled={isLoading}
                  />
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || !password || !confirmPassword}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('resetPassword.resetting', 'Redefinindo...')}
                    </>
                  ) : (
                    t('resetPassword.resetPasswordButton', 'Redefinir senha')
                  )}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>

        <div className="mt-4 flex justify-center">
          <Link
            to="/login"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            {t('resetPassword.backToLogin', 'Voltar para o login')}
          </Link>
        </div>
      </div>
    </div>
  );
}
